import { useQuery } from '@tanstack/react-query'

import { dataOrThrow } from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'

/**
 * React Query key factory for a single pet's live subscription
 * status. Exported so both this hook and the aggregate
 * `usePetsWithSubscriptions` share the same cache entries.
 */
export const petSubscriptionQueryKey = (petId: string) => ['pet-subscription', petId] as const

/**
 * Loads live Stripe subscription state for a single pet via
 * `GET /api/portal/pets/:id/subscription`. Kept per-pet and on-demand
 * so pages can stream membership badges in with their own spinners
 * rather than waiting for a fan-out across every pet.
 */
export function useGetPetSubscription(
  petId: string | null | undefined,
  options: { enabled?: boolean; refetchInterval?: number; refetchOnWindowFocus?: boolean } = {}
) {
  const api = usePortalApi()
  return useQuery({
    queryKey: petSubscriptionQueryKey(petId ?? ''),
    enabled: (options.enabled ?? true) && !!petId,
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    queryFn: async () =>
      dataOrThrow(await api.get('/pets/:id/subscription', { params: { id: petId ?? '' } })),
  })
}
